import { tryCatch } from "@/utils/try-catch";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useForm, UseFormRegister } from "react-hook-form";

import COLIcon from "@/assets/images/col_cc.svg";
import AppCCIcon from "@/assets/images/app_cc.svg";
import CredentialsForm from "@/components/Form/Form";
import CredentialsFooter from "@/components/footer/Footer";
import CredentialsHeader from "@/components/header/Header";
import OptionsGroup from "@/components/option-group/OptionsGroup";
import ErrorMessage from "@/components/error-message/error-message";

import { useApiResponseHandler } from "@/hooks/useApiResponseHandler";
import { FormError } from "@/models/continente-credentials/common/FormError";
import { accountAssociationService } from "@/services/shared/AccountAssociationService";
import { PhoneNumberAssociationResponse } from "@/models/continente-credentials/mobile/PhoneNumberAssociationResponse";
import { AccountAssociationRequest, LoyaltyCardAssociationTypeRequest } from "@/models/shared/AccountAssociationRequest";
import { Button } from "@/components/ui/button";

type Form = AccountAssociationRequest & FormError;

export default function SharedAccountAssociationOptions() {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const location = useLocation<PhoneNumberAssociationResponse>();

  const nextStep = location.state?.nextStep || "";

  const { handleResponse } = useApiResponseHandler();

  const {
    email,
    phoneNumber,
    ccFullName,
    colFullName,
  } = location.state?.properties;

  // const email = "";
  // const phoneNumber = "";
  // const ccFullName = "";
  // const colFullName = "";

  const formState = useForm<Form>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const {
    register,
    setError,
    formState: { errors },
  } = formState;

  const handleWhatIsAccountMergeClick = () => {
    window.open("/continente-credentials/faq", "_blank");
  };

  const handleOptionClick = (option: string) => {
    switch (option) {
      case "col":
        handleMergeCOLClick();
        break;
      case "app_cc":
        handleMergeAPPClick();
        break;
    }
  };

  const handleMergeCOLClick = () => {
    submit(LoyaltyCardAssociationTypeRequest.COL);
  };

  const handleMergeAPPClick = () => {
    submit(LoyaltyCardAssociationTypeRequest.CC);
  };

  const submit = async (associationType: LoyaltyCardAssociationTypeRequest) => {
    setLoading(true);

    const { data: response, error } = await tryCatch(
      accountAssociationService.accountAssociation(
        {
          associationType: associationType,
        },
        nextStep
      )
    );

    handleResponse(
      response,
      { setError },
      error
    );

    setLoading(false);
  };

  useEffect(() => {
    const accountAssociationOptionValidator = (
      register: UseFormRegister<Form>
    ) =>
      register("associationType", {
        required: {
          value: true,
          message: t("common.required_field"),
        },
        pattern: {
          value: /\b(CC|COL)\b/,
          message: t("account_association.not_correct_option"),
        },
      });
    accountAssociationOptionValidator(register);
  }, [register, t]);

  return (
    <CredentialsForm
      id="AccountMerge"
      form={formState}
      autoComplete="off"
    >

      <div className="justify-center">
        <CredentialsHeader
          title={t("account_association.header_title")}
          subtitle={t("account_association.header_message")}
          showLoading={loading}
          previousStepBackXTimes={2}
        />
      </div>
      <div className="justify-center">
        <div className="container">
          <OptionsGroup
            options={[
              {
                text: colFullName != null && colFullName != ""
                  ? t("account_association.option2") +
                  email +
                  t("account_association.option2_1") +
                  " " +
                  colFullName
                  : t("account_association.option2") + email,
                value: "col",
                image: () => <img className="dark:contrast-0" src={COLIcon} alt="Continente online" />,
              },
              {
                text: ccFullName != null && ccFullName != ""
                  ? t("account_association.option1") +
                  phoneNumber +
                  t("account_association.option1_1") +
                  " " +
                  ccFullName
                  : t("account_association.option1") + phoneNumber,
                value: "app_cc",
                image: () => <img className="dark:contrast-0" src={AppCCIcon} alt="App cartão" />,
              },
            ]}
            onSelect={handleOptionClick} disabled={false} />
        </div>
      </div>
      <Button variant={"link"}
        size={"link"} className="font-bold" onClick={handleWhatIsAccountMergeClick}>
        {t("account_association.footer_message")}
      </Button>
      <ErrorMessage error={errors.submitError} />
      <div
        role="note"
        aria-label={t("accessibility.signin.icons")}
        className="flex flex-col">
        <CredentialsFooter
          contextualInformationVisible={false}
          footerAppsWebsitesIconsVisible={false}
        />
      </div>
    </CredentialsForm>
  );
}
